/* eslint-disable class-methods-use-this */
import { getSystem } from '@mx/util/get-system';
import { parseJSON } from '@mx/pay-common';
import type { QueryStrings } from '@mx/util/get-all-query-string';
import { getAllQueryString } from '@mx/util/get-all-query-string';
import type { Runtime } from './constant';
import { OpenType, ReversionId, OpenStatus } from './constant';
import { getRuntime } from './util';

class Env {
  system: string;

  isIOS: boolean;

  isAndroid: boolean;

  runtime: Runtime;

  queryStrings: QueryStrings;

  // common params
  entry: string;

  hideHalfPage: boolean;

  // 支付中签约版本，默认1, 2 为新流程
  reversionId = ReversionId.Init;

  // 基础参数
  inpayBasicBusinessParams: any = {};

  // 通用参数
  commonParams: any = {};

  // 统计/日志参数
  statCommonParams: any = {};

  // 业务参数
  coreBusinessParams: any = {};

  outerBusinessData = '';

  source = '';

  riskParams = '';

  sceneCode = '';

  scorePlanIdFromUrl = ''; // 来自 url 链接

  creditType = '';

  // 参数隧道桥
  access_source = '';

  // 零散业务参数
  iphPayMerchantNo = '';

  queryOrderTradeStatus = '';

  signPayPrams = {} as SignPayParamsType;

  payFailAction = '';

  openType = 0;

  performanceOpenType = 1;

  utmSource = '';

  payDeferStyleType = '';

  // 当hideHalfPage = 1时，utmSource === urlUtmSource
  urlUtmSource = '';

  validateToken = '';

  guideSignParams = {} as GuideSignParamsType;

  guideSignResponseData = {} as GuideSignResponseType;

  specialNeoInitParams = {} as Record<string, any>;

  useNewCashierCallback = false; // 是否使用新的回调方式

  // 流程控制流水号
  bizSerialNo = '';

  // 设置结果页需要的 lx 公共数据
  resultLxCommonParams = {} as Record<string, any>;

  // 保存参数隧道桥时间点
  paramTunnelTime = 0;

  // 鲸湾素材是否配置为免密
  freePassword = false;

  // 支付方式唯一标识 (美团先用后付、微信分后付)
  payType = '';

  // 先用后付or微信分签约状态
  openStatus = OpenStatus.Unopen;

  // 签约微信双联所需参数
  wxAppId = '';

  // 是否能使用NSF
  enableNsf = false;

  /** 请求支付收单返回的交易单号,待支付页用 */
  tradeno = '';

  /** 会员id，后端灰度链路使用 */
  memberId = '';

  /** 多阶段单号 */
  payOutSerialNo = '';

  /** 赊销支付单 */
  payLaterOrderNo = '';

  constructor() {
    const urlParams = getAllQueryString();
    this.queryStrings = getAllQueryString();

    // url params
    this.entry = urlParams.entry || ''; // 相同模版下不同渠道来源
    this.source = urlParams.source || ''; // 打车预付费页面定制参数，业务方固定传 1 - 20211119
    this.riskParams = urlParams.riskParams || ''; // 风控参数，适用于独立签约页面
    this.sceneCode = urlParams.sceneCode || ''; // 账户侧所需参数，需要申请，例如: 单车固定值：MTMobike
    this.scorePlanIdFromUrl = urlParams.scorePlanId || ''; // 独立签约从query 上获取模版id
    this.creditType = urlParams.creditType || 'paydefer'; // 先用后付授信方式 默认为paydefer
    this.hideHalfPage = urlParams.hideHalfPage === '1';
    this.urlUtmSource = urlParams.utm_source;
    this.iphPayMerchantNo = urlParams.iphPayMerchantNo || '';
    this.wxAppId = urlParams.wxAppId || '';
    this.tradeno = urlParams.tradeno || '';
    this.payOutSerialNo = urlParams.payOutSerialNo || '';
    this.payLaterOrderNo = urlParams.payLaterOrderNo || '';

    // app
    this.system = getSystem();
    this.isIOS = this.system === 'ios';
    this.isAndroid = this.system === 'android';

    this.runtime = getRuntime();
  }

  get isIndependenceSign(): boolean {
    return this.openType === OpenType.IndependenceSign;
  }

  getIsLargeHeightPage() {
    const screenWidth = window.screen.width * window.devicePixelRatio;
    if ((this.isIOS && screenWidth <= 750) || (this.isAndroid && screenWidth <= 1080)) {
      return true;
    }
    return false;
  }

  getPayTokenFromHalfPageRenderData(data: Record<string, any>) {
    const initialData: Record<string, any> = parseJSON(data.half_page_render_data?.initialData, () => {}) ?? {};
    return data.pay_token || initialData.pay_token || '';
  }

  getTradeNoFromHalfPageRenderData(data: Record<string, any>) {
    const initialData: Record<string, any> = parseJSON(data.half_page_render_data?.initialData, () => {}) ?? {};
    return data.tradeno || initialData.tradeno || '';
  }

  getExtParamFromHalfPageRenderData(data: Record<string, any>) {
    const initialData: Record<string, any> = parseJSON(data.half_page_render_data?.initialData, () => {}) ?? {};
    return data.ext_param || initialData.ext_param || '';
  }

  /**
   * 设置支付流程基础的业务通用参数
   * @param data 参数隧道
   */
  setInpayBasicBusinessParams(data: NeoCashierParams) {
    this.inpayBasicBusinessParams = {
      pay_token: this.getPayTokenFromHalfPageRenderData(data),
      tradeno: this.getTradeNoFromHalfPageRenderData(data),
    };
  }

  /**
   * 保存全局变量，目前只有 scorePlanId, creditType, authNo
   * 支付流程处理 extra_data 里的数据
   * 独立签约需要自行拼装数据
   * @param data
   */
  setCoreBusinessParams(data: ExtraDataParams) {
    this.coreBusinessParams = data;
  }

  /**
   * 通过半页桥打开本项目，Native会将数据存在参数通道的 halfPageRenderData 字段中
   * @param data
   */
  setAccessSource(data: string) {
    this.access_source = data;
  }

  /**
   * 为适配 Native 增加 outer_business_data 包装
   * 支付流程获取 Native 参数隧道给的 extra_data，直接给到后端，此字段是 JSON 字符串，后端自行解析
   * 独立签约需要前端自行拼接拼装数据
   * @param data
   */
  setOuterBusinessData(data: string) {
    this.outerBusinessData = data;
  }

  /**
   * 通用参数
   * @param data
   */
  setCommonParams(data: Record<string, any>) {
    this.commonParams = {
      token: data.usr_token,
      nb_app: data.app_name,
      nb_appversion: data.app_version,
      nb_platform: data.device_platform,
      nb_ci: data.usr_city_id,
      nb_location: data.usr_location,
      nb_deviceid: data.device_id,
      nb_uuid: data.device_uuid,
      nb_imsi: data.device_imsi,
      nb_channel: data.app_channel,
      sdk_version: data.app_pay_sdk_version,
      nb_fingerprint: data.device_fingerprint,
      nb_osversion: data.device_system_version,
      nb_device_model: data.device_model,
      nb_version: data.app_pay_sdk_version,
      ext_param: this.getExtParamFromHalfPageRenderData(data),
    };
  }

  /**
   * 日志公共参数
   * commonParams 数据量过大此处筛选一部分
   * @param data 参数隧道桥
   */
  setStatCommonParams(data: Record<string, any>) {
    this.statCommonParams = {
      pay_token: this.getPayTokenFromHalfPageRenderData(data),
      tradeno: this.getTradeNoFromHalfPageRenderData(data),
      nb_app: data.app_name || '',
      nb_uuid: data.device_uuid || '',
      sdk_version: data.app_pay_sdk_version || '',
      nb_version: data.app_pay_sdk_version || '',
      last_resumed_page: data.last_resumed_page || '',
      userId: data.usr_id || '',
      merchant_no: data.merchant_no || '',
      nb_platform: data.device_platform,
      stat_time: data.stat_time,
      isOffline: window.__ot__ ? 'offline' : 'online',
      staticVersion: '2.7.1', // 仅为日志排查，离线包排查使用，每次上线手动修改
    };
  }

  /**
   * 保存参数隧道桥返回的特殊业务参数
   * 例如：保存支付后结果页是否通过 Native 唤起,开关状态
   * @param data
   */
  setSpecialNeoInitParams(data: Record<string, any>) {
    this.specialNeoInitParams = {
      promotion_degrade_switch: data.promotion_degrade_switch,
      enable_nsf: data.enable_nsf,
    };
  }

  setUseNewCashierCallback(data: boolean = false) {
    this.useNewCashierCallback = data;
  }

  /**
   * 签约商户号
   * @param data
   */
  setIphPayMerchantNo(data: string = '') {
    this.iphPayMerchantNo = data;
  }

  /**
   * queryorder 订单状态
   * @param data
   */
  setQueryOrderTradeStatus(data: string = '') {
    this.queryOrderTradeStatus = data;
  }

  /**
   * 引导核身接口入参
   * @param data
   */
  setGuideSignParams(data: GuideSignParamsType) {
    this.guideSignParams = data;
  }

  /**
   * 引导核身接口返回值
   * @param data
   */
  setGuideSignResponseData(data: GuideSignResponseType) {
    this.guideSignResponseData = data;
  }

  /**
   * 设置验证密码 token 值
   * @param data
   */
  setValidateToken(data: string) {
    this.validateToken = data;
  }

  /**
   * 是否垫付 signPay 接口标识，协同 queryOrderTradeStatus 使用
   * @param action
   */
  setPayFailAction(action: string) {
    this.payFailAction = action;
  }

  /**
   * 设置签约类型，接入不同页面（0:独立签约, 1:支付流程签约）设置不同类型
   * @param openType
   */
  setOpenType(openType: number) {
    this.openType = openType;
  }

  setPerformanceOpenType() {
    this.performanceOpenType = this.openType + 1;
  }

  /**
   * 保存由鲸湾返回的 utmSource 值，供发券使用
   * @param source
   */
  setUtmSource(source: string) {
    this.utmSource = source;
  }

  /**
   * CTR新版两种样式的标识符 payDeferStyleType
   * @param payDeferStyleType
   */
  setPayDeferStyleType(data: string) {
    this.payDeferStyleType = data;
  }

  /**
   * 支付中签约/独立签约新流程更新标识
   */
  setReversionId(reversionId: number) {
    this.reversionId = reversionId;
  }

  /**
   * 流程控制器接口-授信流水号参数
   */
  setBizSerialNo(bizSerialNo: string) {
    this.bizSerialNo = bizSerialNo;
  }

  setSceneCode(data: string) {
    this.sceneCode = data;
  }

  /**
   * 先用后付结果页LX公共数据
   */
  setResultLxCommonParams(jsData: JsDataType, exposeStrategy: string = 'halfPage') {
    this.resultLxCommonParams = {
      exposeStrategy,
      isOffline: window.__ot__ ? 'offline' : 'online',
      tradeNo: this.inpayBasicBusinessParams.tradeno || '',
      payOrderId: jsData.others.payOrderId,
      pay_order_id: jsData.others.payOrderId,
      nb_version: this.commonParams.nb_version,
      businessType: jsData.others.businessType,
    };
  }

  // 保存参数隧道桥的时间点
  setParamTunnelTime(paramTunnelTime: number) {
    this.paramTunnelTime = paramTunnelTime;
  }

  setFreePasswordFlag(freePassword: boolean) {
    this.freePassword = freePassword || false;
  }

  setPayType(payType: string) {
    this.payType = payType;
  }

  setOpenStatus(openStatus: OpenStatus) {
    this.openStatus = openStatus;
  }

  setEnableNsf(data: boolean) {
    this.enableNsf = data;
  }

  setMemberId(data: Record<string, any>) {
    this.memberId = data.member_id ?? '';
  }
}

export const env = new Env();
